import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api';

function PDFGeneration() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState('');
  const [records, setRecords] = useState([]);
  const [recordRange, setRecordRange] = useState({ start: '', end: '', useRange: false });
  const [batchSize, setBatchSize] = useState(1000);
  const [currentJob, setCurrentJob] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadTemplate();
    loadFiles();
  }, [templateId]);

  useEffect(() => {
    if (selectedFile) {
      loadRecordCount();
    }
  }, [selectedFile]);

  useEffect(() => {
    let interval;
    if (currentJob && currentJob.status === 'processing') {
      interval = setInterval(checkJobStatus, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentJob]);

  const loadTemplate = async () => {
    try {
      const response = await api.get(`/templates/${templateId}`);
      setTemplate(response.data);
    } catch (err) {
      setError('Failed to load template');
      console.error(err);
    }
  };

  const loadFiles = async () => {
    try {
      const response = await api.get('/files');
      setFiles(response.data.filter(file => file.status === 'completed'));
    } catch (err) {
      setError('Failed to load files');
      console.error(err);
    }
  };

  const loadRecordCount = async () => {
    try {
      const response = await api.get(`/records/${selectedFile}?limit=1`);
      setRecords({ total: response.data.total });
    } catch (err) {
      setError('Failed to load record count');
      console.error(err);
    }
  };

  const checkJobStatus = async () => {
    if (!currentJob) return;

    try {
      const response = await api.get(`/pdf-jobs/${currentJob.id}`);
      setCurrentJob(response.data);
    } catch (err) {
      console.error('Failed to check job status:', err);
    }
  };

  const startGeneration = async () => {
    if (!selectedFile) {
      setError('Please select a data file');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const jobData = {
        uploaded_file_id: parseInt(selectedFile),
        template_id: parseInt(templateId),
        records_per_batch: batchSize
      };

      if (recordRange.useRange && recordRange.start && recordRange.end) {
        jobData.start_record_id = parseInt(recordRange.start);
        jobData.end_record_id = parseInt(recordRange.end);
      }

      const response = await api.post('/pdf-jobs', jobData);
      setCurrentJob(response.data);

    } catch (err) {
      setError('Failed to start PDF generation');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelJob = async () => {
    if (!currentJob) return;

    try {
      await api.delete(`/pdf-jobs/${currentJob.id}`);
      setCurrentJob(null);
    } catch (err) {
      setError('Failed to cancel job');
      console.error(err);
    }
  };

  const downloadAllFiles = async () => {
    if (!currentJob || currentJob.status !== 'completed') return;

    try {
      const response = await api.get(`/pdf-jobs/${currentJob.id}/download`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `pdf_batch_${currentJob.id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);

    } catch (err) {
      setError('Failed to download files');
      console.error(err);
    }
  };

  const downloadSingleFile = async (fileId) => {
    try {
      const response = await api.get(`/download/${fileId}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `batch_${fileId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);

    } catch (err) {
      setError('Failed to download file');
      console.error(err);
    }
  };

  const calculateEstimatedBatches = () => {
    if (!records.total) return 0;
    
    let totalRecords = records.total;
    if (recordRange.useRange && recordRange.start && recordRange.end) {
      totalRecords = Math.max(0, parseInt(recordRange.end) - parseInt(recordRange.start) + 1);
    }
    
    return Math.ceil(totalRecords / batchSize);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'processing': return 'text-blue-600 bg-blue-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'cancelled': return 'text-gray-600 bg-gray-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  const getProgressPercentage = () => {
    if (!currentJob || !currentJob.total_batches) return 0;
    return Math.round((currentJob.completed_batches / currentJob.total_batches) * 100);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          Generate PDFs: {template?.name}
        </h1>
        <p className="text-gray-600 mt-2">
          Create PDF documents in batches from your uploaded data
        </p>
      </div>

      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Configuration */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Generation Settings</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data File
                </label>
                <select
                  value={selectedFile}
                  onChange={(e) => setSelectedFile(e.target.value)}
                  disabled={currentJob && currentJob.status === 'processing'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">Choose a file...</option>
                  {files.map(file => (
                    <option key={file.id} value={file.id}>
                      {file.file_name} ({file.total_rows} rows)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Records Per Batch
                </label>
                <select
                  value={batchSize}
                  onChange={(e) => setBatchSize(parseInt(e.target.value))}
                  disabled={currentJob && currentJob.status === 'processing'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value={500}>500 per batch</option>
                  <option value={1000}>1,000 per batch</option>
                  <option value={2000}>2,000 per batch</option>
                  <option value={5000}>5,000 per batch</option>
                </select>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={recordRange.useRange}
                    onChange={(e) => setRecordRange({ ...recordRange, useRange: e.target.checked })}
                    disabled={currentJob && currentJob.status === 'processing'}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Use Record Range
                  </span>
                </label>
              </div>

              {recordRange.useRange && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Record ID
                    </label>
                    <input
                      type="number"
                      value={recordRange.start}
                      onChange={(e) => setRecordRange({ ...recordRange, start: e.target.value })}
                      disabled={currentJob && currentJob.status === 'processing'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      placeholder="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      End Record ID
                    </label>
                    <input
                      type="number"
                      value={recordRange.end}
                      onChange={(e) => setRecordRange({ ...recordRange, end: e.target.value })}
                      disabled={currentJob && currentJob.status === 'processing'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      placeholder={records.total?.toString()}
                    />
                  </div>
                </div>
              )}

              {selectedFile && (
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <div className="text-blue-800">
                    <strong>Estimated Batches:</strong> {calculateEstimatedBatches()}
                  </div>
                  <div className="text-blue-600 mt-1">
                    This will create {calculateEstimatedBatches()} PDF files
                  </div>
                </div>
              )}

              {!currentJob || currentJob.status === 'completed' || currentJob.status === 'failed' ? (
                <button
                  onClick={startGeneration}
                  disabled={!selectedFile || isLoading}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Starting...' : 'Start PDF Generation'}
                </button>
              ) : (
                <button
                  onClick={cancelJob}
                  className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Cancel Job
                </button>
              )}
            </div>
          </div>

          {/* Template Info */}
          {template && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Template Info</h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-gray-600">Paper Size:</span>{' '}
                  <span className="text-gray-900">{template.paper_size}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">Fields:</span>{' '}
                  <span className="text-gray-900">
                    {JSON.parse(template.layout_json).fields?.length || 0}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Job Status and Results */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Generation Status</h3>
              <div className="space-x-2">
                <button
                  onClick={() => navigate(`/preview/${templateId}`)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Preview Template
                </button>
                <button
                  onClick={() => navigate('/templates')}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Back to Templates
                </button>
              </div>
            </div>

            {currentJob ? (
              <div className="space-y-6">
                {/* Job Overview */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <h4 className="text-lg font-medium">Job #{currentJob.id}</h4>
                      <span className={`ml-3 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(currentJob.status)}`}>
                        {currentJob.status.toUpperCase()}
                      </span>
                    </div>
                    {currentJob.status === 'completed' && (
                      <button
                        onClick={downloadAllFiles}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                      >
                        Download All (ZIP)
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-gray-600">Total Records</div>
                      <div className="font-medium">{currentJob.total_records?.toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Total Batches</div>
                      <div className="font-medium">{currentJob.total_batches}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Completed</div>
                      <div className="font-medium">{currentJob.completed_batches}</div>
                    </div>
                    <div>
                      <div className="text-gray-600">Progress</div>
                      <div className="font-medium">{getProgressPercentage()}%</div>
                    </div>
                  </div>

                  {currentJob.status === 'processing' && (
                    <div className="mt-4">
                      <div className="flex justify-between text-sm mb-1">
                        <span>Progress</span>
                        <span>{getProgressPercentage()}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${getProgressPercentage()}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Generated Files */}
                {currentJob.generated_files && currentJob.generated_files.length > 0 && (
                  <div>
                    <h4 className="text-lg font-medium mb-3">Generated Files</h4>
                    <div className="space-y-2">
                      {currentJob.generated_files.map((file) => (
                        <div key={file.id} className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(file.status)}`}>
                                {file.status}
                              </span>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                Batch #{file.batch_number}
                              </div>
                              <div className="text-sm text-gray-500">
                                {file.file_size ? `${(file.file_size / 1024).toFixed(1)} KB` : 'Processing...'}
                              </div>
                            </div>
                          </div>
                          {file.status === 'completed' && (
                            <button
                              onClick={() => downloadSingleFile(file.id)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Download
                            </button>
                          )}
                          {file.status === 'failed' && file.error_message && (
                            <div className="text-xs text-red-600 max-w-xs truncate" title={file.error_message}>
                              Error: {file.error_message}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Generation Job Active</h3>
                <p className="text-gray-500">
                  Configure your settings and click "Start PDF Generation" to begin creating your PDFs.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PDFGeneration;
